import React, { useCallback } from "react";
import { AgGrid, AgGridReact, EmptyState, Flex, Grid } from "@jsq/ds/core";
import { useDistributionWorkflowContext } from "../../../Context/context";
import { emptyStateContainer } from "../styles_vanilla.css";
import { getColumnDefinitions } from "./columnDefinitions";
import { selectPositionsTable } from "./styles_vanilla.css";
import { PositionRowData, SelectPositionTableProps } from "./types";

const defaultColumnDef: AgGrid.GridOptions["defaultColDef"] = {
  flex: 1,
  minWidth: 100,
  editable: false,
};

const gridOptions: AgGridReact.AgGridReactProps["gridOptions"] = {
  enableCellTextSelection: true, // Allows text to be copied from cells
  suppressCellFocus: true,
  suppressContextMenu: true, // Prevents right clicking menu pop up on cells
  rowMultiSelectWithClick: true, // Without this, clicking on a row deselects all other rows
};

export const SelectPositionsTable: React.FC<SelectPositionTableProps> = ({
  allowSelection,
  emptyTableMessage,
  handleSelectionChange,
  loading,
  positionRowData,
  showStatusColumn = false,
}: SelectPositionTableProps) => {
  const { selectedPositions } = useDistributionWorkflowContext();

  if (allowSelection) {
    gridOptions.rowSelection = "multiple";
  } else {
    gridOptions.rowSelection = undefined;
  }

  const onSelectionChange = useCallback(
    (event: AgGrid.SelectionChangedEvent<PositionRowData>) => {
      if (allowSelection && handleSelectionChange) {
        const gridApi = event.api;
        const selectedRows = gridApi.getSelectedRows();
        handleSelectionChange(selectedRows);
      }
    },
    [allowSelection, handleSelectionChange],
  );

  const onFirstDataRendered = useCallback(
    ({ api }: AgGrid.FirstDataRenderedEvent<PositionRowData>) => {
      if (allowSelection) {
        const positionIdSet = new Set(selectedPositions.map((p) => p.positionId));
        api.forEachNode((node) => {
          // If selectedPositions already exist in context, we should maintain it.
          // Otherwise default to selecting everything whose payment instructions have been NOT been updated
          if (positionIdSet.size > 0) {
            if (node.data?.positionId && positionIdSet.has(node.data.positionId)) {
              node.setSelected(true);
            }
          } else {
            if (!node.data?.paymentInstructionsRecentlyUpdated) {
              node.setSelected(true);
            }
          }
        });
      }
    },
    [allowSelection, selectedPositions],
  );

  const onGridReady = useCallback(
    ({ api }: AgGrid.GridReadyEvent) => {
      if (loading) {
        api.showLoadingOverlay();
      }
    },
    [loading],
  );

  return (
    <>
      {!loading && positionRowData.length === 0 ? (
        <Flex className={emptyStateContainer}>
          <EmptyState illustration="man-with-new" title={emptyTableMessage} />
        </Flex>
      ) : (
        <Flex data-testid="positionsTable" className={selectPositionsTable}>
          <Grid<PositionRowData>
            suppressMovableColumns // suppress column moving, i.e. to make the columns fixed position
            suppressMultiSort // suppress multi-sort when the user shift-clicks a column header. Does not currently work
            rowData={loading ? null : positionRowData} // don't pass in rowData if loading
            // This prevents AG Grid from thinking there is no data and
            // showing the no rows overlay when data is in fact still loading.
            onGridReady={onGridReady}
            gridOptions={gridOptions}
            columnDefs={getColumnDefinitions(allowSelection, showStatusColumn)}
            defaultColDef={defaultColumnDef}
            pagination={true}
            paginationPageSize={50}
            paginationAutoPageSize={false}
            paginationPageSizeSelector={false} // disabling to keep consistent with rest of workflow
            domLayout="autoHeight" // grid's height is set to fix number of rows so no scrollbar needed for table
            onSelectionChanged={onSelectionChange}
            onFirstDataRendered={onFirstDataRendered}
            tooltipShowDelay={0} // show tooltips immediately
          />
        </Flex>
      )}
    </>
  );
};
